import { AnimatePresence, motion } from 'motion/react';
import clsx from 'clsx';
import { Flex, Text, Icon, iconWeight, Portal } from '@nula/core-ui';
import styles from './expandable-bar.module.css';

export interface ExpandableBarPill {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
}

export interface ExpandableBarProps {
  icon: React.ReactNode;
  placeholder: string;
  pills?: ExpandableBarPill[];
  onExpand: () => void;
  className?: string;
  visible?: boolean;
}

export function ExpandableBar({
  icon,
  placeholder,
  pills,
  onExpand,
  className,
  visible = true,
}: ExpandableBarProps) {
  return (
    <Portal>
      <AnimatePresence>
        {visible && (
          <motion.div
            className={clsx(styles.root, className)}
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 10, opacity: 0 }}
            transition={{ type: 'spring', damping: 28, stiffness: 220 }}
          >
            <div className={styles.ringWrap} data-slot="companion-perch-assistant" data-companion-surface="perch">
              {/* Rotating aurora ring: a large conic-gradient square spins
                  behind the bar (Framer Motion). The bar sits on top inset by a
                  fixed amount on all sides, so the exposed gradient band is a
                  uniform-width ring — equal thickness on the straight edges and
                  the rounded ends. */}
              <motion.div
                className={styles.ringSpinner}
                aria-hidden
                animate={{ rotate: 360 }}
                transition={{ duration: 6, ease: 'linear', repeat: Infinity }}
              />
              <Flex
                align="center"
                gap="sm"
                className={styles.bar}
                onClick={onExpand}
                role="button"
                aria-label={placeholder}
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onExpand(); } }}
              >
                <Flex align="center" justify="center" className={styles.iconSlot}>
                  {icon}
                </Flex>
                <Text size="sm" className={styles.placeholder}>{placeholder}</Text>
                {pills && pills.length > 0 && (
                  <Flex align="center" gap="xs" className={styles.pillsRow}>
                    {pills.map((pill, i) => (
                      <Flex
                        key={i}
                        align="center"
                        gap="xs"
                        className={styles.pill}
                        onClick={(e) => { e.stopPropagation(); pill.onClick(); }}
                        role="button"
                        tabIndex={0}
                      >
                        {pill.icon}
                        <Text size="xs">{pill.label}</Text>
                      </Flex>
                    ))}
                  </Flex>
                )}
                <Flex align="center" className={styles.chevron}>
                  <Icon name="expand_less" size={16} weight={iconWeight.light} />
                </Flex>
              </Flex>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Portal>
  );
}
